import { RewardUnlockedProps } from './reward-unlocked.interface';
import Lottie from 'lottie-react';
import confettiLottie from 'src/assets/animations/lottie/confetti.json';
import Button from 'src/components/Button/Button';
import { ButtonSize } from 'src/components/Button/button-size.enum';
import { ButtonState } from 'src/components/Button/button-state.enum';
import { ButtonType } from 'src/components/Button/button-type.enum';
import { RewardProgress } from 'src/components/GamificationPanel/RewardProgress/RewardProgress';
import Modal from 'src/components/Modal/Modal';
import { useRewardWithdrawalContext } from 'src/contexts/RewardWithdrawalContext/RewardWithdrawalContext';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';

/**
 * RewardUnlocked modal component.
 */
const RewardUnlocked: React.FC<RewardUnlockedProps> = ({ isOpen, onClose, rewardProgress }) => {
  const t = useTranslationByKey('COMPONENTS.REWARD_UNLOCKED_OVERLAY');
  const { setIsReadyToRedeemOpen } = useRewardWithdrawalContext();
  const { isMobile } = useBreakpoint();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      hideCloseButton={true}
      className={isMobile ? '!w-full box-content !rounded-3xl' : '!rounded-3xl'}
      modalBodyClassName={
        'md:w-[420px] !bg-gradient-to-br !from-blue-50 !via-indigo-50 !to-purple-50 !border-2 !border-white/40 !shadow-2xl overflow-hidden'
      }
    >
      {/* Confetti Lottie Animation - background, only when open */}
      {isOpen && (
        <div className="absolute inset-0 z-50 pointer-events-none overflow-hidden opacity-40">
          <Lottie animationData={confettiLottie} loop={false} autoplay style={{ width: '100%', height: '100%' }} />
        </div>
      )}
      <div className="relative">
        <div className="relative z-10 flex flex-col gap-6 p-6 md:p-8">
          {/* Enhanced Celebration Header with better visual hierarchy */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              <h1 className="text-2xl md:text-3xl font-black leading-tight mb-2">{t('TITLE')}</h1>
            </div>
          </div>

          <div>
            {rewardProgress && (
              <div className="transform hover:scale-105 transition-transform duration-300 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100 shadow-sm">
                <RewardProgress {...rewardProgress} shouldAnimate={false} showRewardImage={true} pointsEarned={0} />
              </div>
            )}
          </div>

          {/* Enhanced Action Buttons with better spacing and effects */}
          <div className="flex flex-col gap-4">
            <Button
              label={<span className="flex items-center justify-center gap-2 font-bold">{t('BUTTON_REDEEM')}</span>}
              onClick={() => {
                setIsReadyToRedeemOpen(true);
                onClose();
              }}
              state={ButtonState.Enabled}
              type={ButtonType.Filled}
              size={ButtonSize.Big}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl border-2 border-blue-400"
            />

            <Button
              label={<span className="flex items-center justify-center gap-2">{t('BUTTON_CONTINUE')}</span>}
              onClick={() => {
                onClose();
              }}
              state={ButtonState.Enabled}
              type={ButtonType.Ghost}
              size={ButtonSize.Big}
              animated={false}
              className="w-full bg-white/40 backdrop-blur-sm border-2 border-indigo-200 text-indigo-700 font-medium rounded-xl hover:bg-white/60 hover:border-indigo-300 transition-all duration-300 transform hover:scale-102"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RewardUnlocked;
